import Box from '@mui/material/Box';
import { daDK } from '@mui/x-data-grid/locales';
import * as React from 'react';

import { Avatar } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { getTypeColor, getTypeIcon } from '../../../../utils/nodeTypeMapper';

import StandardAnalysisToolbar from './StandardAnalysisToolbar';

const displayTime = (time) => {
  if (!time) {
    return '';
  }
  const hours = Math.floor(time);
  let minutes = Math.round((time % 1) * 60);
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  return `${hours}:${minutes}`;
};

const formatTimeRange = (start, end) => {
  const startTime = displayTime(start);
  const endTime = displayTime(end);
  return { startTime, endTime };
};

const formatDays = (daysOfWeek, isActive) => {
  const dayNames = {
    monday: 'Mandag',
    tuesday: 'Tirsdag',
    wednesday: 'Onsdag',
    thursday: 'Torsdag',
    friday: 'Fredag',
    saturday: 'Lørdag',
    sunday: 'Søndag',
  };
  return (
    Object.entries(daysOfWeek)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, active]) => active === isActive)
      .map(([day]) => dayNames[day.toLowerCase()] || day)
      .join(', ')
  );
};

export default function StandardAnalysisDatagrid(props) {
  const {
    initialRows = [],
    isLoading = true,
    selectedEnergyGroup = {},
  } = props;
  const [showPercentage, setShowPercentage] = React.useState(false);

  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});

  const visibilityModels = React.useMemo(() => {
    const percentageModel = {
      Name: true,
      Type: true,
      TotalEnergyUsage: false,
      Share: true,
      ActivePeriod: false,
      ActivePeriodPercentage: true,
      InactivePeriod: false,
      InactivePeriodPercentage: true,
      ActiveDays: false,
      ActiveDaysPercentage: true,
      InactiveDays: false,
      InactiveDaysPercentage: true,
    };

    const consumptionModel = {
      Name: true,
      Type: true,
      TotalEnergyUsage: true,
      Share: false,
      ActivePeriod: true,
      ActivePeriodPercentage: false,
      InactivePeriod: true,
      InactivePeriodPercentage: false,
      ActiveDays: true,
      ActiveDaysPercentage: false,
      InactiveDays: true,
      InactiveDaysPercentage: false,
    };

    return showPercentage ? percentageModel : consumptionModel;
  }, [showPercentage]);

  React.useEffect(() => {
    setColumnVisibilityModel(visibilityModels);
  }, [visibilityModels]);

  const activeTime = React.useMemo(() => {
    if (
      !selectedEnergyGroup?.daysOfWeek ||
      !selectedEnergyGroup.workingDayStart ||
      !selectedEnergyGroup.workingDayEnd
    )
      return '';
    const { startTime, endTime } = formatTimeRange(
      selectedEnergyGroup.workingDayStart,
      selectedEnergyGroup.workingDayEnd,
    );
    const days = formatDays(selectedEnergyGroup.daysOfWeek, true);

    return `Aktive timer er ${startTime}-${endTime}, på dagene: ${days}, dette kan ændres i opsætning.`;
  }, [
    selectedEnergyGroup.daysOfWeek,
    selectedEnergyGroup.workingDayEnd,
    selectedEnergyGroup.workingDayStart,
  ]);

  const inactiveTime = React.useMemo(() => {
    if (
      !selectedEnergyGroup?.daysOfWeek ||
      !selectedEnergyGroup.workingDayStart ||
      !selectedEnergyGroup.workingDayEnd
    )
      return '';
    const { startTime, endTime } = formatTimeRange(
      selectedEnergyGroup.workingDayStart,
      selectedEnergyGroup.workingDayEnd,
    );
    const days = formatDays(selectedEnergyGroup.daysOfWeek, true);
    const inactiveDays = formatDays(selectedEnergyGroup.daysOfWeek, false);

    return `Inaktive timer er 00:00-${startTime} og ${endTime}-23:59, på dagene: ${days}, og hele ${inactiveDays} dette kan ændres i opsætning.`;
  }, [
    selectedEnergyGroup.daysOfWeek,
    selectedEnergyGroup.workingDayEnd,
    selectedEnergyGroup.workingDayStart,
  ]);

  const activeDays = React.useMemo(() => {
    if (!selectedEnergyGroup?.daysOfWeek) return '';
    const days = formatDays(selectedEnergyGroup.daysOfWeek, true);
    return `Aktive dage: ${days}, dette kan ændres i opsætning.`;
  }, [selectedEnergyGroup]);

  const inactiveDays = React.useMemo(() => {
    if (!selectedEnergyGroup?.daysOfWeek) return '';
    const days = formatDays(selectedEnergyGroup.daysOfWeek, false);
    return `Inaktive dage: ${days}, dette kan ændres i opsætning.`;
  }, [selectedEnergyGroup]);

  const initialState = {
    sorting: {
      sortModel: [
        {
          field: 'TotalEnergyUsage',
          sort: 'desc',
        },
      ],
    },
  };

  const columns = [
    {
      field: 'icon',
      headerName: '',
      maxWidth: 60,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Avatar
              variant="rounded"
              sx={{
                backgroundColor: getTypeColor(params.value) || '#555',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: 2,
              }}
            >
              <i className={`fas ${getTypeIcon(params.value)}`}></i>
            </Avatar>
          </Box>
        );
      },
    },
    {
      field: 'Name',
      headerName: 'Navn',
      type: 'string',
    },
    {
      field: 'Type',
      headerName: 'Type',
    },
    {
      field: 'TotalEnergyUsage',
      headerName: 'Forbrug',
      type: 'number',
      valueFormatter: (value) => {
        return (
          value.toLocaleString('da-DK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + ' kWh'
        );
      },
    },
    {
      field: 'Share',
      headerName: 'Andel',
      type: 'number',
      valueFormatter: (value) => {
        return (
          (value * 100).toLocaleString('da-DK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + '%'
        );
      },
    },
    {
      field: 'ActivePeriod',
      headerName: 'Aktive timer',
      type: 'number',
      description: activeTime,
      valueFormatter: (value) => {
        return (
          value.toLocaleString('da-DK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + ' kWh'
        );
      },
    },
    {
      field: 'ActivePeriodPercentage',
      headerName: 'Aktive timer',
      type: 'number',
      description: activeTime,
      valueFormatter: (value) => {
        return (
          (value * 100).toLocaleString('da-DK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + '%'
        );
      },
    },
    {
      field: 'InactivePeriod',
      headerName: 'Inaktive timer',
      type: 'number',
      description: inactiveTime,
      valueFormatter: (value) => {
        return (
          value.toLocaleString('da-DK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + ' kWh'
        );
      },
    },
    {
      field: 'InactivePeriodPercentage',
      headerName: 'Inaktive timer',
      type: 'number',
      description: inactiveTime,
      valueFormatter: (value) => {
        return (
          (value * 100).toLocaleString('da-DK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + '%'
        );
      },
    },
    {
      field: 'ActiveDays',
      headerName: 'Aktive dage',
      type: 'number',
      description: activeDays,
      valueFormatter: (value) => {
        return (
          value.toLocaleString('da-DK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + ' kWh'
        );
      },
    },
    {
      field: 'ActiveDaysPercentage',
      headerName: 'Aktive dage',
      type: 'number',
      description: activeDays,
      valueFormatter: (value) => {
        return (
          (value * 100).toLocaleString('da-DK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + '%'
        );
      },
    },
    {
      field: 'InactiveDays',
      headerName: 'Inaktive dage',
      type: 'number',
      description: inactiveDays,
      valueFormatter: (value) => {
        return (
          value.toLocaleString('da-DK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + ' kWh'
        );
      },
    },
    {
      field: 'InactiveDaysPercentage',
      headerName: 'Inaktive dage',
      type: 'number',
      description: inactiveDays,
      valueFormatter: (value) => {
        return (
          (value * 100).toLocaleString('da-DK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + '%'
        );
      },
    },
  ].map((column) => {
    return { ...column, flex: 1 };
  });

  return (
    <Box sx={{ height: '100%' }}>
      <DataGridPremium
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
        rows={initialRows}
        columns={columns}
        initialState={initialState}
        loading={isLoading}
        sortingOrder={['desc', 'asc', null]}
        slots={{ toolbar: StandardAnalysisToolbar }}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'skeleton',
          },
          toolbar: {
            setShowPercentage: setShowPercentage,
            showPercentage: showPercentage,
          },
        }}
        sx={{
          border: 'none',
          borderRadius: 2,
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDatagrid-root': {
            border: 0,
          },
          '.MuiDataGrid-withBorderColor': {
            border: 'none !important',
          },
          '& .MuiDatagrid-footerContainer': {
            border: 0,
          },
          '& .MuiDatagrid-bottomContainer': {
            border: 0,
          },
          '& .MuiDataGrid-main': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#fff' : '#121212',
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .MuiDataGrid-columnsContainer': {
            backgroundColor: 'blue',
          },
          '& .MuiDataGrid-columnHeaders': {
            // backgroundColor: 'blue', // Background color for column headers
            // color: 'white', // Text color
            // fontSize: '1rem', // Adjust font size if needed
            borderBottom: '1px solid', // Optional: Add a border
            borderColor: 'divider',
          },
          '& .selected-row': {
            backgroundColor: (theme) => theme.palette.action.selected,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.selected,
            },
          },
        }}
      />
    </Box>
  );
}
